import { createSignal, For, JSXElement, Show } from 'solid-js';
import { Button } from '~/components/ui/Button';
import Modal from '~/components/ui/Modal';
import { ModalSizes, ModalTypes } from '~/components/ui/Modal/Modal.tsx';
import { useAppData } from '~/components/AppData';
import FSAOnboardingLogo from '~/assets/fsa-onboarding-logo.svg';
import './fsaOnboardingModal.module.scss';
import { Localized } from '~/i18n';
import { ArrowUpRightIcon, CircleCheckIcon } from 'lucide-solid';
import Link from '~/components/Link';
import { externalLinks } from '~/consts.ts';
import {
  EnvironmentTypes,
  getEnvActiveSsoConnectionListQueryKey,
  retryAuthorizedRequests,
  searchConnections,
  SearchConnectionsResponse
} from '~/api';
import {
  enableFSAWithSuccessToast,
  FSA_FEATURES_TRANSLATION_KEYS,
  getTranslationKey,
  isOnEnvironmentQuickStart,
  logAndToastFSAError
} from '~/pages/FSAOnboarding/utils.tsx';
import { useIntl } from '@cookbook/solid-intl';
import { useFSAModalContext } from '~/pages/FSAOnboarding/FSAModalContext.tsx';
import { useToast } from '~/components/ui/Toast';
import { useQuery } from '@tanstack/solid-query';
import SkLoader from '~/components/ui/SkLoader';
import {
  EnvironmentPagePath,
  getSlug,
  replacePathParams
} from '~/components/AppRouter/utils.ts';
import { QuickStartModuleTypes } from '~/pages/Quickstart/utils.ts';
import { QuickstartFsaSteps } from '~/pages/Quickstart/QuickStartFSA/utils.ts';
import { useNavigate } from '@solidjs/router';

export default function FSAOnboardingModal(): JSXElement {
  const appData = useAppData();
  const intl = useIntl();
  const toaster = useToast();
  const navigate = useNavigate();
  const fsaModalContext = useFSAModalContext();
  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [isImageLoading, setIsImageLoading] = createSignal<boolean>(true);

  const activeSSOConnectionsQuery = useQuery(() => ({
    queryKey: getEnvActiveSsoConnectionListQueryKey(
      appData.selectedEnvironment?.id
    ),
    queryFn: async () => {
      return await searchConnections({
        enabled: true
      });
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  async function getActiveSsoConnectionCount(
    isProdEnv: boolean
  ): Promise<number> {
    await activeSSOConnectionsQuery.refetch();

    const connections =
      activeSSOConnectionsQuery.data as SearchConnectionsResponse;

    return isProdEnv
      ? connections.totalSize
      : Math.max(0, connections.totalSize - 1);
  }

  const closeAllModals = (closeModal: () => void) => {
    closeModal();
    fsaModalContext.hideFSAOnboardingModal();
  };

  const navigateToFsaQuickstart = () => {
    const envId = appData.selectedEnvironment?.id;
    if (!envId) return;
    navigate(
      getSlug.environmentById(
        envId,
        replacePathParams(
          EnvironmentPagePath.ENVIRONMENT_FSA_QUICK_START_MODULE,
          {
            moduleType: QuickStartModuleTypes.FSA,
            step: QuickstartFsaSteps.CONFIGURE
          }
        ) as EnvironmentPagePath,
        true
      )
    );
  };

  return (
    <Modal
      open={fsaModalContext.isFSAOnboardingModalOpen}
      size={ModalSizes.XXX_LARGE}
      type={ModalTypes.DIALOG}
    >
      {(closeModal) => (
        <>
          <div class={`flex gap-6`}>
            <div class={`min-h-[380px] min-w-[509px] relative`}>
              <Show when={isImageLoading()}>
                <SkLoader />
              </Show>
              <img
                src={FSAOnboardingLogo}
                alt={'Full-Stack Auth Spotlight Banner'}
                loading="eager"
                fetchpriority="high"
                onLoad={() => setIsImageLoading(false)}
              />
            </div>
            <div class={`flex flex-col justify-between grow`}>
              <div class={`flex flex-col gap-5`}>
                <h2 class={`text-md font-semibold`}>
                  <Localized translationKey={getTranslationKey(`header`)} />
                </h2>
                <p class={`text-sm`}>
                  <Localized
                    translationKey={getTranslationKey(
                      `fsa_features_description`
                    )}
                  />
                </p>
                <ul class={`flex flex-col gap-5`}>
                  <For each={FSA_FEATURES_TRANSLATION_KEYS}>
                    {(translationKey) => (
                      <li class={`pl-7 relative flex items-center`}>
                        <div class={`absolute left-0`}>
                          <CircleCheckIcon
                            width={24}
                            class={`fill-[#30A46C] text-bg-default`}
                          />
                        </div>
                        <Localized translationKey={translationKey} />
                      </li>
                    )}
                  </For>
                </ul>
              </div>
              <div class={`flex justify-between gap-4`}>
                <Link
                  href={externalLinks.kbArticles.fsaQuickstartGuide}
                  target={'_blank'}
                  class={`flex items-center text-sm text-link-primary hover:underline`}
                  data-testid={'fsa-banner-docs'}
                >
                  <Localized
                    translationKey={getTranslationKey(`fsa_docs_button_label`)}
                  />
                  <ArrowUpRightIcon
                    strokeWidth={1.5}
                    width={16}
                    class={`mt-[1px]`}
                  />
                </Link>
                <div class={`flex gap-4`}>
                  <Button
                    onClick={() => {
                      closeModal();
                      fsaModalContext.hideFSAOnboardingModal();
                    }}
                    variant={`outline`}
                    size={'sm'}
                    data-testid={'fsa-banner-cancel'}
                  >
                    <Localized translationKey={'cta_labels.cancel'} />
                  </Button>
                  <Button
                    variant={`solid`}
                    size={'sm'}
                    loading={isLoading()}
                    data-testid={'fsa-banner-activate'}
                    onClick={async () => {
                      let shouldNavigateToQuickStartFsa =
                        isOnEnvironmentQuickStart();

                      try {
                        setIsLoading(true);
                        const isProdEnv =
                          appData.selectedEnvironment!.type ===
                          EnvironmentTypes.PRODUCTION;

                        const activeConnections =
                          await getActiveSsoConnectionCount(isProdEnv);

                        if (activeConnections > 0) {
                          closeAllModals(closeModal);

                          const confirmed =
                            await fsaModalContext.confirmFsaOnboardingWarningModal();

                          if (!confirmed) {
                            shouldNavigateToQuickStartFsa = false;
                            return;
                          }
                        } else {
                          await enableFSAWithSuccessToast({
                            envId: appData.selectedEnvironment!.id,
                            toaster,
                            intl
                          });
                        }
                      } catch (error) {
                        logAndToastFSAError({
                          error: error as Error,
                          toaster,
                          intl
                        });
                        shouldNavigateToQuickStartFsa = false;
                      } finally {
                        setIsLoading(false);
                        closeAllModals(closeModal);
                        if (shouldNavigateToQuickStartFsa) {
                          navigateToFsaQuickstart();
                        }
                      }
                    }}
                  >
                    <Localized
                      translationKey={'cta_labels.setup_full_stack_auth'}
                    />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
